import { useAtom } from 'jotai';
import {
  useAuthoriseForPicking,
  useGoodsReceivedNote,
  useInboundProcess,
  useOutboundProcess,
  useOutboundShipmentNote,
  usePickingNote,
  useProofOfDelivery,
} from '@hooks';
import { AuthoriseForPickingForm } from '@forms/manager/AuthoriseForPickingForm';
import { ConfirmPickingNoteForm } from '@forms/manager/ConfirmPickingNoteForm';
import { CreateDraftGoodsReceivedNoteForm } from '@forms/manager/CreateDraftGoodsReceivedNoteForm';
import { CreateOutboundShipmentNoteForm } from '@forms/manager/CreateOutboundShipmentNoteForm';
import { CreateProofOfDeliveryForm } from '@forms/manager/CreateProofOfDeliveryForm';
import { SignOffGoodsReceivedNoteForm } from '@forms/manager/SignOffGoodsReceivedNoteForm';
import { Modal } from '../../../tailwindComponents/Modal';
import {
  isInboundSidebarOpenAtom,
  isOutboundSidebarOpenAtom,
} from '../../../store/jotai';
import { SupplyChainProcessModal } from './SupplyChainProcessModal';
import { WarehouseClientInboundSideDrawer } from './WarehouseClientInboundSideDrawer';
import { WarehouseClientOutboundSideDrawer } from './WarehouseClientOutboundSideDrawer';
import { WarehouseManagerInboundSideDrawer } from './WarehouseManagerInboundSideDrawer';
import { WarehouseManagerOutboundSideDrawer } from './WarehouseManagerOutboundSideDrawer';

interface SupplyChainProcessDrawersProps {
  isLsp: boolean;
  isDesktop: boolean;
  isTablet: boolean;
  inboundProcess: ReturnType<typeof useInboundProcess>;
  outboundProcess: ReturnType<typeof useOutboundProcess>;
  goodsReceivedNote: ReturnType<typeof useGoodsReceivedNote>;
  authoriseForPicking: ReturnType<typeof useAuthoriseForPicking>;
  pickingNote: ReturnType<typeof usePickingNote>;
  outboundShipmentNote: ReturnType<typeof useOutboundShipmentNote>;
  proofOfDelivery: ReturnType<typeof useProofOfDelivery>;
}

export function SupplyChainProcessDrawers({
  isLsp,
  isDesktop,
  isTablet,
  inboundProcess,
  outboundProcess,
  goodsReceivedNote,
  authoriseForPicking,
  pickingNote,
  outboundShipmentNote,
  proofOfDelivery,
}: SupplyChainProcessDrawersProps) {
  const [isInboundSidebarOpen, setIsInboundSidebarOpen] = useAtom(
    isInboundSidebarOpenAtom
  );
  const [isOutboundSidebarOpen, setIsOutboundSidebarOpen] = useAtom(
    isOutboundSidebarOpenAtom
  );

  const {
    selectedInboundProcess,
    setSelectedInboundProcess,
    selectedInboundProcessHasDescription,
    isViewShipmentNoteDialogOpen,
    setIsViewShipmentNoteDialogOpen,
    isViewGoodsReceivedNoteDialogOpen,
    setIsViewGoodsReceivedNoteDialogOpen,
    selectedProcessChildType,
    setSelectedProcessChildType,
  } = inboundProcess;

  const {
    selectedOutboundProcess,
    setSelectedOutboundProcess,
    selectedOutboundProcessHasDescription,
    isViewDespatchNoteDialogOpen,
    setIsViewDespatchNoteDialogOpen,
    isViewPickingConfirmationDialogOpen,
    setIsViewPickingConfirmationDialogOpen,
    isViewOutboundShipmentNoteDialogOpen,
    setIsViewOutboundShipmentNoteDialogOpen,
    isViewProofOfDeliveryDialogOpen,
    setIsViewProofOfDeliveryDialogOpen,
  } = outboundProcess;

  const {
    isCreateDraftGoodsReceivedNoteFormOpen,
    setIsCreateDraftGoodsReceivedNoteFormOpen,
    handleSubmitCreateDraftGoodsReceivedNote,
    isSignOffGoodsReceivedNoteFormOpen,
    setIsSignOffGoodsReceivedNoteFormOpen,
    handleSubmitSignOffGoodsReceivedNote,
  } = goodsReceivedNote;

  const {
    isAuthoriseForPickingFormOpen,
    setIsAuthoriseForPickingFormOpen,
    handleSubmitAuthoriseForPickingForm,
  } = authoriseForPicking;

  const {
    isConfirmPickingNoteFormOpen,
    setIsConfirmPickingNoteFormOpen,
    handleSubmitConfirmPickingNote,
  } = pickingNote;

  const {
    isCreateOutboundShipmentNoteFormOpen,
    setIsCreateOutboundShipmentNoteFormOpen,
    handleSubmitOutboundShipmentNote,
  } = outboundShipmentNote;

  const {
    isCreateProofOfDeliveryFormOpen,
    setIsCreateProofOfDeliveryFormOpen,
    handleSubmitCreateProofOfDelivery,
  } = proofOfDelivery;

  return (
    <>
      {/* Sidebar for selected inbound process */}
      {!isLsp && (
        <WarehouseClientInboundSideDrawer
          selectedInboundProcess={selectedInboundProcess}
          setSelectedInboundProcess={setSelectedInboundProcess}
          isOpen={isInboundSidebarOpen}
          setIsOpen={setIsInboundSidebarOpen}
          isDesktop={isDesktop}
          selectedInboundProcessHasDescription={
            selectedInboundProcessHasDescription
          }
          setIsViewShipmentNoteDialogOpen={setIsViewShipmentNoteDialogOpen}
          setIsViewGoodsReceivedNoteDialogOpen={
            setIsViewGoodsReceivedNoteDialogOpen
          }
          selectedProcessChildType={selectedProcessChildType}
          setSelectedProcessChildType={setSelectedProcessChildType}
        />
      )}
      {isLsp && (
        <WarehouseManagerInboundSideDrawer
          selectedInboundProcess={selectedInboundProcess}
          setSelectedInboundProcess={setSelectedInboundProcess}
          selectedInboundProcessHasDescription={
            selectedInboundProcessHasDescription
          }
          isOpen={isInboundSidebarOpen}
          setIsOpen={setIsInboundSidebarOpen}
          isDesktop={isDesktop}
          setIsViewShipmentNoteDialogOpen={setIsViewShipmentNoteDialogOpen}
          setIsSignOffGoodsReceivedNoteFormOpen={
            setIsSignOffGoodsReceivedNoteFormOpen
          }
          setIsCreateDraftGoodsReceivedNoteFormOpen={
            setIsCreateDraftGoodsReceivedNoteFormOpen
          }
          setIsViewGoodsReceivedNoteDialogOpen={
            setIsViewGoodsReceivedNoteDialogOpen
          }
          selectedProcessChildType={selectedProcessChildType}
          setSelectedProcessChildType={setSelectedProcessChildType}
        />
      )}
      {/* Sidebar for selected outbound process */}
      {!isLsp && (
        <WarehouseClientOutboundSideDrawer
          selectedOutboundProcess={selectedOutboundProcess}
          setSelectedOutboundProcess={setSelectedOutboundProcess}
          isOpen={isOutboundSidebarOpen}
          setIsOpen={setIsOutboundSidebarOpen}
          isDesktop={isDesktop}
          selectedOutboundProcessHasDescription={
            selectedOutboundProcessHasDescription
          }
          setIsViewDespatchNoteDialogOpen={setIsViewDespatchNoteDialogOpen}
          setIsViewPickingConfirmationDialogOpen={
            setIsViewPickingConfirmationDialogOpen
          }
          setIsViewOutboundShipmentNoteDialogOpen={
            setIsViewOutboundShipmentNoteDialogOpen
          }
          setIsViewProofOfDeliveryDialogOpen={
            setIsViewProofOfDeliveryDialogOpen
          }
          selectedProcessChildType={selectedProcessChildType}
          setSelectedProcessChildType={setSelectedProcessChildType}
        />
      )}
      {isLsp && (
        <WarehouseManagerOutboundSideDrawer
          selectedOutboundProcess={selectedOutboundProcess}
          setSelectedOutboundProcess={setSelectedOutboundProcess}
          selectedOutboundProcessHasDescription={
            selectedOutboundProcessHasDescription
          }
          isOpen={isOutboundSidebarOpen}
          setIsOpen={setIsOutboundSidebarOpen}
          isDesktop={isDesktop}
          setIsViewDespatchNoteDialogOpen={setIsViewDespatchNoteDialogOpen}
          setIsAuthoriseForPickingFormOpen={setIsAuthoriseForPickingFormOpen}
          setIsConfirmPickingNoteFormOpen={setIsConfirmPickingNoteFormOpen}
          setIsViewPickingConfirmationDialogOpen={
            setIsViewPickingConfirmationDialogOpen
          }
          setIsCreateOutboundShipmentNoteFormOpen={
            setIsCreateOutboundShipmentNoteFormOpen
          }
          setIsViewOutboundShipmentNoteDialogOpen={
            setIsViewOutboundShipmentNoteDialogOpen
          }
          setIsCreateProofOfDeliveryFormOpen={
            setIsCreateProofOfDeliveryFormOpen
          }
          setIsViewProofOfDeliveryDialogOpen={
            setIsViewProofOfDeliveryDialogOpen
          }
          selectedProcessChildType={selectedProcessChildType}
          setSelectedProcessChildType={setSelectedProcessChildType}
        />
      )}

      {/* View shipment note */}
      {selectedInboundProcess && isViewShipmentNoteDialogOpen && (
        <SupplyChainProcessModal
          process={selectedInboundProcess}
          isOpen={isViewShipmentNoteDialogOpen}
          setIsOpen={setIsViewShipmentNoteDialogOpen}
          initialStepKey='shipment'
          isDesktop={isDesktop}
          isTablet={isTablet}
        />
      )}
      {/* View signed-off goods received note */}
      {selectedInboundProcess && isViewGoodsReceivedNoteDialogOpen && (
        <SupplyChainProcessModal
          process={selectedInboundProcess}
          isOpen={isViewGoodsReceivedNoteDialogOpen}
          setIsOpen={setIsViewGoodsReceivedNoteDialogOpen}
          initialStepKey='grn'
          isDesktop={isDesktop}
          isTablet={isTablet}
        />
      )}

      {/* LSP-only forms: */}
      {/* Create draft goods received note form */}
      {isLsp && (
        <Modal
          isOpen={isCreateDraftGoodsReceivedNoteFormOpen}
          setIsOpen={setIsCreateDraftGoodsReceivedNoteFormOpen}
          title='Create Draft Goods Received Note'
        >
          <CreateDraftGoodsReceivedNoteForm
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            selectedInboundProcess={selectedInboundProcess!}
            onClose={() => setIsCreateDraftGoodsReceivedNoteFormOpen(false)}
            onSubmit={handleSubmitCreateDraftGoodsReceivedNote}
            isDesktop={isDesktop}
            isTablet={isTablet}
          />
        </Modal>
      )}
      {/* Sign off goods received note form */}
      {isLsp && (
        <Modal
          isOpen={isSignOffGoodsReceivedNoteFormOpen}
          setIsOpen={setIsSignOffGoodsReceivedNoteFormOpen}
          title='Draft Goods Received Note'
        >
          <SignOffGoodsReceivedNoteForm
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            selectedInboundProcess={selectedInboundProcess!}
            onClose={() => setIsSignOffGoodsReceivedNoteFormOpen(false)}
            onSubmit={handleSubmitSignOffGoodsReceivedNote}
            isDesktop={isDesktop}
            isTablet={isTablet}
          />
        </Modal>
      )}
      {/* Authorise for picking form */}
      {isLsp && (
        <Modal
          isOpen={isAuthoriseForPickingFormOpen}
          setIsOpen={setIsAuthoriseForPickingFormOpen}
          title='Authorise For Picking'
        >
          <AuthoriseForPickingForm
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            selectedOutboundProcess={selectedOutboundProcess!}
            onClose={() => setIsAuthoriseForPickingFormOpen(false)}
            onSubmit={handleSubmitAuthoriseForPickingForm}
            isDesktop={isDesktop}
            isTablet={isTablet}
          />
        </Modal>
      )}
      {/* Confirm picking note form */}
      {isLsp && (
        <Modal
          isOpen={isConfirmPickingNoteFormOpen}
          setIsOpen={setIsConfirmPickingNoteFormOpen}
          title='Confirm Picking'
        >
          <ConfirmPickingNoteForm
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            selectedOutboundProcess={selectedOutboundProcess!}
            onClose={() => {
              setIsConfirmPickingNoteFormOpen(false);
            }}
            onSubmit={handleSubmitConfirmPickingNote}
            isDesktop={isDesktop}
            isTablet={isTablet}
          />
        </Modal>
      )}
      {/* Create outbound shipment note form */}
      {isLsp && (
        <Modal
          isOpen={isCreateOutboundShipmentNoteFormOpen}
          setIsOpen={setIsCreateOutboundShipmentNoteFormOpen}
          title='Create Outbound Shipment Note'
        >
          <CreateOutboundShipmentNoteForm
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            selectedOutboundProcess={selectedOutboundProcess!}
            onClose={() => {
              setIsCreateOutboundShipmentNoteFormOpen(false);
            }}
            onSubmit={handleSubmitOutboundShipmentNote}
            isDesktop={isDesktop}
            isTablet={isTablet}
          />
        </Modal>
      )}
      {/* Create proof of delivery form */}
      {isLsp && (
        <Modal
          isOpen={isCreateProofOfDeliveryFormOpen}
          setIsOpen={setIsCreateProofOfDeliveryFormOpen}
          title='Create Proof Of Delivery'
        >
          <CreateProofOfDeliveryForm
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            selectedOutboundProcess={selectedOutboundProcess!}
            onClose={() => {
              setIsCreateOutboundShipmentNoteFormOpen(false);
            }}
            onSubmit={handleSubmitCreateProofOfDelivery}
            isDesktop={isDesktop}
            isTablet={isTablet}
          />
        </Modal>
      )}

      {/* View despatch note details */}
      {selectedOutboundProcess && isViewDespatchNoteDialogOpen && (
        <SupplyChainProcessModal
          process={selectedOutboundProcess}
          isOpen={isViewDespatchNoteDialogOpen}
          setIsOpen={setIsViewDespatchNoteDialogOpen}
          initialStepKey='despatch'
          isDesktop={isDesktop}
          isTablet={isTablet}
        />
      )}
      {/* View picking confirmation */}
      {selectedOutboundProcess && isViewPickingConfirmationDialogOpen && (
        <SupplyChainProcessModal
          process={selectedOutboundProcess}
          isOpen={isViewPickingConfirmationDialogOpen}
          setIsOpen={setIsViewPickingConfirmationDialogOpen}
          initialStepKey='picking'
          isDesktop={isDesktop}
          isTablet={isTablet}
        />
      )}
      {/* View outbound shipment note */}
      {selectedOutboundProcess && isViewOutboundShipmentNoteDialogOpen && (
        <SupplyChainProcessModal
          process={selectedOutboundProcess}
          isOpen={isViewOutboundShipmentNoteDialogOpen}
          setIsOpen={setIsViewOutboundShipmentNoteDialogOpen}
          initialStepKey='shipment'
          isDesktop={isDesktop}
          isTablet={isTablet}
        />
      )}
      {/* View proof of delivery */}
      {selectedOutboundProcess && isViewProofOfDeliveryDialogOpen && (
        <SupplyChainProcessModal
          process={selectedOutboundProcess}
          isOpen={isViewProofOfDeliveryDialogOpen}
          setIsOpen={setIsViewProofOfDeliveryDialogOpen}
          initialStepKey='delivery'
          isDesktop={isDesktop}
          isTablet={isTablet}
        />
      )}
    </>
  );
}
